// Theme get/set/toggle for the admin app — a copy of the buyer app's
// `web/src/lib/theme.ts` + `theme-client.ts` pattern (shared has no home for it
// yet), collapsed into one module because this SPA has no server-component
// split. LIGHT-FIRST, exactly like Mira: anything that isn't an explicit "dark"
// is light. The pre-paint script in index.html reads the same cookie/storage
// key before first paint so there's no flash.
//
// Deviation from web (deliberate): app-private cookie/storage names. Cookies
// ignore ports, so on localhost the buyer app (5173) and this app (5376) would
// share `hitch-theme` — the same cross-app bleed the identity keys avoid
// (ARCHITECTURE.md: app-private `recruiter_admin_app_*` keys).
import { useCallback, useEffect, useState } from "react";

export type Theme = "dark" | "light";

export const THEME_COOKIE_NAME = "recruiter_admin_app_theme";
export const THEME_STORAGE_KEY = "recruiter_admin_app_theme";
// One year — long enough that the preference survives tab cleanup.
export const THEME_COOKIE_MAX_AGE_S = 60 * 60 * 24 * 365;
// Same-tab theme broadcast: `storage` events only reach OTHER tabs, so
// applyTheme dispatches this for sibling useTheme() instances in this one.
export const THEME_CHANGE_EVENT = "recruiter-admin-theme-change";

export function isTheme(value: unknown): value is Theme {
  return value === "light" || value === "dark";
}

function readCookieTheme(): Theme | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split(";")
    .map((p) => p.trim())
    .find((p) => p.startsWith(`${THEME_COOKIE_NAME}=`));
  if (!match) return null;
  const value = match.slice(THEME_COOKIE_NAME.length + 1);
  return isTheme(value) ? value : null;
}

function writeCookieTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${THEME_COOKIE_NAME}=${theme}; path=/; max-age=${THEME_COOKIE_MAX_AGE_S}; SameSite=Lax${secure}`;
}

function readStoredTheme(): Theme | null {
  if (typeof window === "undefined") return null;
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(value) ? value : null;
  } catch {
    return null;
  }
}

function writeStoredTheme(theme: Theme): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* noop — incognito / quota errors are fine, cookie is the durable source */
  }
}

function readDocumentTheme(): Theme {
  // Light-first (Mira): anything that isn't an explicit "dark" is light.
  if (typeof document === "undefined") return "light";
  const attr = document.documentElement.getAttribute("data-theme");
  return attr === "dark" ? "dark" : "light";
}

export function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
  writeCookieTheme(theme);
  writeStoredTheme(theme);
  // Same-tab broadcast so sibling useTheme() instances re-read — the
  // `storage` event below only fires in OTHER tabs.
  window.dispatchEvent(new Event(THEME_CHANGE_EVENT));
}

// React hook — keeps the current theme in component state and exposes
// setters. Subscribes to cross-tab `storage` events so flipping the
// theme in one tab updates the others without a refresh.
export function useTheme(): { theme: Theme; setTheme: (t: Theme) => void; toggle: () => void } {
  const [theme, setThemeState] = useState<Theme>(() => readDocumentTheme());

  useEffect(() => {
    // Re-sync from the DOM after mount in case the pre-paint script applied a
    // stored theme that differed from the initial render's assumption.
    setThemeState(readDocumentTheme());

    const onStorage = (e: StorageEvent) => {
      if (e.key !== THEME_STORAGE_KEY) return;
      const next = isTheme(e.newValue) ? e.newValue : "light";
      document.documentElement.setAttribute("data-theme", next);
      setThemeState(next);
    };
    const onThemeChange = () => setThemeState(readDocumentTheme());
    window.addEventListener("storage", onStorage);
    window.addEventListener(THEME_CHANGE_EVENT, onThemeChange);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(THEME_CHANGE_EVENT, onThemeChange);
    };
  }, []);

  const setTheme = useCallback((next: Theme) => {
    applyTheme(next);
    setThemeState(next);
  }, []);

  const toggle = useCallback(() => {
    const next = readDocumentTheme() === "light" ? "dark" : "light";
    applyTheme(next);
    setThemeState(next);
  }, []);

  return { theme, setTheme, toggle };
}

// Read the preference outside of React.
export function getStoredTheme(): Theme {
  return readCookieTheme() ?? readStoredTheme() ?? readDocumentTheme();
}
